import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { apiGet, apiPost, setTokenGetter } from '@/auth/api-client';
import { BootstrapContext } from '@/auth/bootstrap-context';
import AgreementPage from '@/pages/Agreement';
import LegacyMigrationPage from '@/pages/LegacyMigration';
import { migrateFromLocalStorage, runMigrations, setCurrentUserId, loadLegacyBudgets, applyLegacyDecisions, hasLegacyBudgets, loadBudgets, saveBudgets, } from '@/storage/storage';
import { setSyncCursor, setServerDeviceId } from '@/storage/sync-meta';
import { applyRemoteToBudgets, EMPTY_DELETIONS } from '@/sync/remote-apply';
import App from './App';
const LAST_USER_ID_KEY = 'budgetcal_last_user_id';
function isNetworkError(err) {
    if (err instanceof TypeError && err.message === 'Failed to fetch')
        return true;
    const apiErr = err;
    return apiErr.status === undefined || apiErr.status === 0;
}
export default function AppShell() {
    const auth = useAuth();
    const [phase, setPhase] = useState('auth_check');
    const [bootstrapData, setBootstrapData] = useState(null);
    const [legacyBudgets, setLegacyBudgets] = useState([]);
    const [error, setError] = useState(null);
    const [agreementContents, setAgreementContents] = useState([]);
    const [agreementLoading, setAgreementLoading] = useState(false);
    const [agreementError, setAgreementError] = useState(null);
    const [legacyLoading, setLegacyLoading] = useState(false);
    const [offline, setOffline] = useState(!navigator.onLine);
    const initRef = useRef(false);
    // Track online/offline status
    useEffect(() => {
        const goOnline = () => setOffline(false);
        const goOffline = () => setOffline(true);
        window.addEventListener('online', goOnline);
        window.addEventListener('offline', goOffline);
        return () => {
            window.removeEventListener('online', goOnline);
            window.removeEventListener('offline', goOffline);
        };
    }, []);
    // Wire up token getter for api-client
    useEffect(() => {
        setTokenGetter(() => auth.user?.access_token ?? null);
    }, [auth.user]);
    // Phase: AUTH_CHECK
    useEffect(() => {
        if (phase !== 'auth_check')
            return;
        if (auth.isLoading)
            return;
        if (auth.isAuthenticated) {
            setPhase('bootstrap');
            return;
        }
        // Not authenticated — check if we can go offline with a cached user
        const lastUserId = localStorage.getItem(LAST_USER_ID_KEY);
        if (auth.error) {
            // Auth failed — go offline if possible, otherwise show error
            if (lastUserId) {
                setCurrentUserId(lastUserId);
                setOffline(true);
                setPhase('ready');
            }
            else {
                setError(`Ошибка авторизации: ${auth.error.message}`);
            }
            return;
        }
        // Try redirect to login; if it fails (server down), fall back to offline
        auth.signinRedirect().catch(() => {
            if (lastUserId) {
                setCurrentUserId(lastUserId);
                setOffline(true);
                setPhase('ready');
            }
            else {
                setError('Сервер авторизации недоступен. Подключитесь к интернету для первого входа.');
            }
        });
    }, [phase, auth]);
    // Phase: BOOTSTRAP
    useEffect(() => {
        if (phase !== 'bootstrap')
            return;
        if (initRef.current)
            return;
        initRef.current = true;
        (async () => {
            try {
                const data = await apiGet('/api/v1/me/bootstrap');
                setBootstrapData(data);
                // Save userId for offline fallback
                setCurrentUserId(data.profile.userId);
                localStorage.setItem(LAST_USER_ID_KEY, data.profile.userId);
                // Гидрируем полный снапшот облачных данных из bootstrap в IndexedDB ДО
                // установки курсора. Иначе новое устройство стартует с cursor=head,
                // инкрементальный pull ничего не тянет, и облачные бюджеты не появляются.
                // Локальные (LocalOnly) бюджеты при merge не затрагиваются.
                try {
                    const localBudgets = await loadBudgets();
                    const merged = applyRemoteToBudgets(localBudgets, {
                        budgets: data.budgets ?? [],
                        categories: data.categories ?? [],
                        transactions: data.transactions ?? [],
                    }, EMPTY_DELETIONS);
                    await saveBudgets(merged);
                }
                catch {
                    // сбой гидрирования не должен блокировать вход
                }
                // Save sync cursor and server device ID from bootstrap
                if (data.cursor !== undefined && data.cursor !== null) {
                    setSyncCursor(data.cursor).catch(() => { });
                }
                if (data.device?.deviceId) {
                    setServerDeviceId(data.device.deviceId).catch(() => { });
                }
                const pending = data.agreements.filter((a) => a.mustBlockUsageUntilAccepted && !a.isAccepted);
                if (pending.length > 0) {
                    // Load agreement content for pending agreements
                    const contentPromises = pending.map((a) => apiGet(`/api/v1/agreements/${a.agreementCode}/content`).catch(() => null));
                    const loadedContents = (await Promise.all(contentPromises)).filter((c) => c !== null);
                    setAgreementContents(loadedContents);
                    setPhase('agreements');
                }
                else {
                    setPhase('legacy_check');
                }
            }
            catch (err) {
                if (isNetworkError(err)) {
                    // Network error — try offline mode
                    const lastUserId = localStorage.getItem(LAST_USER_ID_KEY);
                    if (lastUserId) {
                        setCurrentUserId(lastUserId);
                        setOffline(true);
                        setPhase('legacy_check');
                        return;
                    }
                    setError('Сервер недоступен. Войдите при наличии интернета для первой настройки.');
                    return;
                }
                const apiErr = err;
                if (apiErr.status === 428) {
                    setPhase('agreements');
                }
                else {
                    setError(`Ошибка загрузки: ${apiErr.errors?.[0]?.message ?? 'Сервер недоступен'}`);
                }
            }
        })();
    }, [phase]);
    // Phase: LEGACY_CHECK
    useEffect(() => {
        if (phase !== 'legacy_check')
            return;
        (async () => {
            await migrateFromLocalStorage();
            await runMigrations();
            const hasLegacy = await hasLegacyBudgets();
            if (hasLegacy && !offline) {
                const budgets = await loadLegacyBudgets();
                setLegacyBudgets(budgets);
                return;
            }
            setPhase('ready');
        })();
    }, [phase, offline]);
    const handleAcceptAgreements = useCallback(async () => {
        const pending = (bootstrapData?.agreements ?? []).filter((a) => a.mustBlockUsageUntilAccepted && !a.isAccepted);
        setAgreementLoading(true);
        setAgreementError(null);
        try {
            const acceptances = pending.map((a) => ({
                agreementCode: a.agreementCode,
                version: a.requiredVersion,
                acceptedAtUtc: new Date().toISOString(),
            }));
            await apiPost('/api/v1/agreements/accept', { acceptances });
            initRef.current = false;
            setPhase('bootstrap');
        }
        catch {
            setAgreementError('Не удалось принять соглашение. Попробуйте ещё раз.');
        }
        finally {
            setAgreementLoading(false);
        }
    }, [bootstrapData]);
    const handleLegacyConfirm = useCallback(async (decisions) => {
        setLegacyLoading(true);
        await applyLegacyDecisions(decisions);
        setLegacyBudgets([]);
        setLegacyLoading(false);
        setPhase('ready');
    }, []);
    const refreshBootstrap = useCallback(async () => {
        const data = await apiGet('/api/v1/me/bootstrap');
        setBootstrapData(data);
    }, []);
    if (error) {
        return (_jsxs("div", { style: { padding: 32, textAlign: 'center' }, children: [_jsx("p", { children: error }), _jsx("button", { onClick: () => window.location.reload(), children: "\u041F\u043E\u043F\u0440\u043E\u0431\u043E\u0432\u0430\u0442\u044C \u0441\u043D\u043E\u0432\u0430" })] }));
    }
    if (phase === 'auth_check' || phase === 'bootstrap') {
        return (_jsx("div", { style: { padding: 32, textAlign: 'center' }, children: _jsx("p", { children: "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430..." }) }));
    }
    if (phase === 'agreements') {
        return (_jsx(AgreementPage, { agreements: bootstrapData?.agreements ?? [], contents: agreementContents, loading: agreementLoading, error: agreementError, onAccept: handleAcceptAgreements }));
    }
    if (phase === 'legacy_check' && legacyBudgets.length > 0) {
        return (_jsx(LegacyMigrationPage, { budgets: legacyBudgets, loading: legacyLoading, onConfirm: handleLegacyConfirm }));
    }
    if (phase === 'ready') {
        return (_jsx(BootstrapContext.Provider, { value: { data: bootstrapData, refresh: refreshBootstrap, offline }, children: _jsx(App, {}) }));
    }
    return null;
}
